import { TimePicker } from 'antd';
import locale from 'antd/es/date-picker/locale/ja_JP';
import { DefaultOptionType } from 'antd/es/select';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { STATUS_NAME, StatusName } from '@pages/project-management/detail/constant';
import { renderTaskStatus } from '@pages/project-management/detail/utils/helper';
import { ATTACHMENT_STATUS, DEADLINE_STATUS } from '@pages/project-management/task/constants';
import {
  IDetailTaskData,
  IFormMOT,
  IFormStakeHolder,
  IOptions,
  IStatus,
  ProjectTaskPic,
  StatusCategory
} from '@pages/project-management/task/models';
import { TaskDetailContext } from '@pages/project-management/task/view';

import { BasePopupProps, showBasePopup } from '@components/base-popup/BasePopup';
import { FormInputDate } from '@components/form-input-date/FormInputDate';
import FormMultipleSelect, { IConfirmCallbackParams } from '@components/form-select/FormMultipleSelect';
import { FormSelect } from '@components/form-select/FormSelect';
import SelectionPreview from '@components/form-select/SelectionPreview';

import useFetch from '@hooks/useFetch';
import useMutation from '@hooks/useMutation';

import { DataViewer } from '@/utils/helpers/common';
import { CATEGORY } from '@utils/constants';
import { API } from '@utils/constants/Apis';
import { FORMAT_DATE_EN, FORMAT_DATE_EN_HH_MM_SS, FORMAT_DATE_TIME_EN, FORMAT_TIME } from '@utils/constants/AppConstants';
import { HTTP_STATUS_CODE } from '@utils/constants/Http';

type Props = {
  detailData: IDetailTaskData;
  isEdit?: boolean;
  fetchData: () => Promise<void>;
};

type TASK_TYPE_SHOW_PIC = typeof CATEGORY.CS | typeof CATEGORY.SUPPORT | typeof CATEGORY.MOT;

export const TaskDetailQuickUpdateForm = (props: Props) => {
  const { detailData, isEdit, fetchData } = props;
  const { t } = useTranslation();
  const { idProject, idTask } = useParams();

  const { setIsWarningDeadline } = useContext(TaskDetailContext);
  const [timeOnly, setTimeOnly] = useState<Dayjs | null>(null);

  const { mutate: updateDeadline } = useMutation(API.UPDATE_DEADLINE_TASK_DETAIL(idProject ?? '', idTask ?? ''), {
    method: 'PUT',
    bodyType: 'json',
    showToastError: true,
    showToastSuccess: true,
    defaultSuccessMsg: t('common:MSG_C_003', { item: t('button:keep') }) ?? ''
  });

  const queryStakeHolder = useRef({ query: 'activeOnly' });
  const stakeHolderEndpoint = detailData && detailData.taskType === CATEGORY.OTHER ? API.GET_STAKEHOLDERS_BY_PROJECT(idProject ?? '') : '';
  const { data: stakeHolderEmails } = useFetch<IFormStakeHolder[]>(stakeHolderEndpoint, 'GET', queryStakeHolder.current);
  const userPicEndpoint =
    detailData && [CATEGORY.CS, CATEGORY.SUPPORT, CATEGORY.MOT].includes(detailData.taskType as TASK_TYPE_SHOW_PIC)
      ? API.GET_USERS_MOT_PIC(true)
      : '';
  const { data: userPics } = useFetch<IFormMOT[]>(userPicEndpoint, 'GET');
  const { mutate: updatePic } = useMutation(API.UPDATE_PIC_TASK_DETAIL(idProject ?? '', idTask ?? ''), {
    method: 'PUT',
    bodyType: 'json',
    showToastError: true,
    showToastSuccess: true,
    defaultSuccessMsg: t('common:MSG_C_003', { item: t('button:keep') }) ?? '',
    defaultErrorMsg: 'MSG_C_019',
    paramMsg: {
      field: t('project:task_of_project:mot_pick:label')
    }
  });
  const { data: listStatus } = useFetch<IStatus[]>('/prj/status?type=task', 'GET');
  const { mutate: updateStatus } = useMutation(API.UPDATE_STATUS_TASK_DETAIL(idProject ?? '', idTask ?? ''), {
    method: 'PUT',
    bodyType: 'json',
    showToastError: true,
    showToastSuccess: true,
    defaultSuccessMsg: t('common:MSG_C_003', { item: t('button:keep') }) ?? ''
  });

  const { watch, setValue, reset, resetField } = useFormContext();

  const isTaskDelay = useMemo(() => {
    if (detailData?.statusName === STATUS_NAME.CANCELLED || detailData?.statusName === STATUS_NAME.COMPLETED) {
      return false;
    }

    if (!detailData?.deadline) return false;

    const deadlineDate = dayjs(detailData.deadline);

    if (detailData.deadlineTime) {
      const time = dayjs(detailData.deadlineTime, 'HH:mm:ss');
      const fullDeadline = deadlineDate.hour(time.hour()).minute(time.minute()).second(time.second());

      return fullDeadline.isBefore(dayjs(), 'second');
    }

    return deadlineDate.isBefore(dayjs(), 'day');
  }, [detailData?.statusName, detailData?.deadline, detailData?.deadlineTime]);

  const documents = detailData?.taskRequireDocuments ?? [];

  const allFiles = documents.flatMap((doc) => doc.taskRequireDocumentAttachmentFiles ?? []);

  const hasWaitingFile = allFiles.some((file) => file.status === ATTACHMENT_STATUS.WAITING);

  const hasRejectedFile = allFiles.some((file) => file.status === ATTACHMENT_STATUS.REJECT);

  const hasAnyProcessing = documents.some((doc) => {
    const files = doc.taskRequireDocumentAttachmentFiles ?? [];
    const hasFile = files.length > 0;
    const hasWaiting = files.some((f) => f.status === ATTACHMENT_STATUS.WAITING);

    return hasFile && (!doc.isConfirm || hasWaiting);
  });

  const hasDocWithoutFile = documents.some((doc) => {
    const files = doc.taskRequireDocumentAttachmentFiles ?? [];
    return files.length === 0;
  });

  const displayStatus: StatusName = (() => {
    if (hasAnyProcessing) {
      return STATUS_NAME.MOT_CONFIRM;
    }

    if (hasDocWithoutFile || isTaskDelay || hasRejectedFile) {
      return STATUS_NAME.DELAYED;
    }

    return STATUS_NAME.DELAYED;
  })();

  const isSupportTask = detailData?.taskType === CATEGORY.SUPPORT;

  const picOptions = (): {
    [key: string]: IOptions[];
  } => {
    const results: {
      [key: string]: IOptions[];
    } = {
      all: [],
      filtered: []
    };
    if (!detailData) return results;
    if (detailData.taskType === CATEGORY.OTHER) {
      results.all =
        stakeHolderEmails?.map((f: IFormStakeHolder) => ({
          id: f.stakeHolderId,
          label: f.stakeHolderName,
          value: f.stakeHolderEmail
        })) ?? [];
      results.filtered = results.all;
    } else if ([CATEGORY.CS, CATEGORY.SUPPORT, CATEGORY.MOT].includes(detailData.taskType as TASK_TYPE_SHOW_PIC)) {
      userPics?.forEach((f: IFormMOT) => {
        results.all.push({
          id: f.id ?? '',
          label: f.name,
          value: f.email
        });
        if (f.status)
          results.filtered.push({
            id: f.id ?? '',
            label: f.name,
            value: f.email
          });
      });
    }
    return results;
  };
  const statusOptions = listStatus?.reduce((options: DefaultOptionType[], item: IStatus) => {
    if (!detailData) {
      return [];
    }
    if (isSupportTask) {
      options.push({
        label: item.name,
        value: item.id
      });
    } else if (!item.types?.includes(StatusCategory.TASK_SUPPORT)) {
      options.push({
        label: item.name,
        value: item.id
      });
    }
    return options;
  }, []);

  const handleChangeDeadline = async (value: Dayjs | null, includeTime?: boolean) => {
    try {
      if (!value) {
        const result = await updateDeadline({
          deadline: null,
          deadlineTime: null
        });
        if (result?.status === HTTP_STATUS_CODE.SUCCESS) {
          fetchData();
          setTimeOnly(null);
          setValue('deadline', null);
        }
        return;
      }

      const payload: any = {
        deadline: dayjs(value).format(FORMAT_DATE_EN_HH_MM_SS)
      };

      const shouldIncludeTime = includeTime ?? timeOnly;

      if (shouldIncludeTime) {
        payload.deadlineTime = dayjs(value).format('HH:mm');
      }

      const result = await updateDeadline(payload);

      if (result?.status === HTTP_STATUS_CODE.SUCCESS) {
        fetchData();
      }
    } catch (error) {}
  };

  const handleChangeTime = async (time: Dayjs | null) => {
    const deadline = watch('deadline');
    if (!deadline) return;

    const base = dayjs(deadline);

    let newDate;
    let shouldIncludeTime = false;

    if (time) {
      shouldIncludeTime = true;
      setTimeOnly(time);
      newDate = base.hour(time.hour()).minute(time.minute()).second(0);
    } else {
      setTimeOnly(null);
      newDate = base.startOf('day');
    }

    setValue('deadline', newDate);
    await handleChangeDeadline(newDate, shouldIncludeTime);
  };

  const handleOnChangePic = (selectedValues: (ProjectTaskPic & IOptions)[]) => {
    const items = selectedValues.map((item: any) => {
      return {
        label: item.label,
        value: item.value
      };
    });
    setValue('picId', items);
  };

  const handlePicCondition = async () => {
    const content: BasePopupProps = {
      type: 'confirm',
      title: t('project:task_of_project:change_pic:title') || '',
      msg: t('project:task_of_project:change_pic:content')
    };
    return showBasePopup(content);
  };

  const handleConfirmPic = async ({ removed, added, original }: IConfirmCallbackParams<ProjectTaskPic & IOptions>) => {
    try {
      const picPayloads: Partial<ProjectTaskPic & IOptions>[] = [];
      removed.forEach((item) => {
        const matched = original.find((i) => i.value === item.value);
        if (!matched) return;
        picPayloads.push({ ...matched, picEmail: item.value, deleted: true });
      });
      added.forEach((item) => {
        picPayloads.push({ picEmail: item.value });
      });
      const result = await updatePic({ pics: picPayloads });
      if (result?.status === HTTP_STATUS_CODE.SUCCESS) {
        await fetchData();
      } else {
        reset((prev) => ({
          ...prev,
          picId: original
        }));
      }
    } catch (error) {}
  };

  const handleCancelPic = () => {
    const originValues =
      detailData.projectTaskPics.map((item) => ({
        ...item,
        label: item.picName,
        value: item.picEmail
      })) ?? [];
    reset((prev) => ({
      ...prev,
      picId: originValues
    }));
  };

  const handleChangeStatus = async (value: string) => {
    try {
      const result = await updateStatus({ statusId: value });
      if (result?.status === HTTP_STATUS_CODE.SUCCESS) fetchData();
      else resetField('status', { defaultValue: detailData?.statusName });
    } catch (errors) {}
  };

  const handleShowConfirmChangeStatus = async (newVal: string) => {
    let content: BasePopupProps = {
      type: 'confirm',
      title: t('project:task_of_project:change_status:title') || '',
      msg: t('common:MSG_C_021', { item: t('project:task_of_project:status:label') || '' })
    };
    const hasRequireDocuments = detailData.taskRequireDocuments?.length > 0;
    const hasInternalDocuments = detailData.taskInternalDocuments?.length > 0;
    const oldStatus = watch('status');
    const oldStatusLabel = statusOptions?.find((item) => item.value === oldStatus)?.label;
    const newStatusLabel = statusOptions?.find((item) => item.value === newVal)?.label;
    const hasUnsubmittedDoc =
      detailData.taskInternalDocuments?.some((item: any) => !item.isConfirm) || detailData.taskRequireDocuments?.some((item: any) => !item.isConfirm);

    const isMotOrOther = detailData?.taskType === CATEGORY.MOT || detailData?.taskType === CATEGORY.OTHER;

    if (
      isMotOrOther &&
      !hasRequireDocuments &&
      !hasInternalDocuments &&
      oldStatusLabel === STATUS_NAME.TODO &&
      newStatusLabel === STATUS_NAME.IN_PROGRESS
    ) {
      content.msg = t('common:MSG_C_021_1');
    } else if (isMotOrOther && hasUnsubmittedDoc && newStatusLabel === STATUS_NAME.COMPLETED) {
      content.msg = t('common:MSG_C_021_2');
    } else if (!detailData?.projectTaskPics?.length) {
      content.msg = t('common:MSG_P_031');
    }

    if (!detailData?.deadline) setIsWarningDeadline?.(true);
    else {
      const response = await showBasePopup(content);
      if (response === 'confirm') {
        handleChangeStatus(newVal);
      } else {
        setValue('status', oldStatus);
        throw new Error('cancel');
      }
    }
  };

  const handlePreChangeStatus = async (newVal: string) => {
    handleShowConfirmChangeStatus(newVal);
  };

  const deadline = watch('deadline');

  const timeValue = useMemo(() => {
    return timeOnly;
  }, [timeOnly]);

  const messageWarning = useMemo(() => {
    if ((!detailData?.deadline && detailData?.statusName === STATUS_NAME.TODO) || !detailData?.departureDate) return '';
    if (!deadline && detailData?.statusName !== STATUS_NAME.TODO) return DEADLINE_STATUS.ERROR;
    if (!!detailData?.deadline) {
      const isWarning = dayjs(detailData?.deadline).isAfter(dayjs(detailData?.departureDate));
      if (!isWarning) return;
      return DEADLINE_STATUS.WARNING;
    }
  }, [detailData, deadline]);

  const deadlineValue = useMemo(() => {
    if (!detailData?.deadline) return undefined;

    const date = dayjs(detailData.deadline);
    const time = dayjs(detailData.deadlineTime, 'HH:mm:ss');

    return date.hour(time.hour()).minute(time.minute()).second(time.second());
  }, [detailData]);

  useEffect(() => {
    if (!detailData?.deadline) {
      setValue('deadline', null);
      setTimeOnly(null);
      return;
    }

    const date = dayjs(detailData.deadline);

    if (detailData.deadlineTime) {
      const time = dayjs(detailData.deadlineTime, 'HH:mm');

      setValue('deadline', date.hour(time.hour()).minute(time.minute()).second(0));
      setTimeOnly(time);
    } else {
      setValue('deadline', date.startOf('day'));
      setTimeOnly(null);
    }
  }, [detailData]);

  return (
    <div className='flex gap-[16px] justify-end'>
      <div className='flex gap-[8px]'>
        <div className='text-lnk text-[14px] font-medium flex mt-[12px] w-max'>{t('project:task_of_project:deadline')}</div>
        <div className={isSupportTask && isEdit ? 'w-[252px]' : 'w-[208px]'}>
          <FormInputDate
            name='deadline'
            labelTx=''
            defaultValue={deadlineValue as any}
            triggerShowModal='both'
            allowClear={true}
            format={FORMAT_DATE_TIME_EN}
            customMessage={() => {
              if (!messageWarning) return { status: '', content: '' };
              if (messageWarning === DEADLINE_STATUS.ERROR) {
                return {
                  status: DEADLINE_STATUS.ERROR,
                  content: (
                    <div className='text-[14px] font-normal leading-[22px] h-[26px] mt-0 text-negative'>
                      {t('common:MSG_001_textbox', { field: t('project:task_of_project:deadline') })}
                    </div>
                  )
                };
              }
              return {
                status: DEADLINE_STATUS.WARNING,
                content: (
                  <div className='text-[14px] font-normal leading-[22px] mt-0 text-warning'>
                    {t('project:task_of_project:warning_deadline_after_departure_date')}
                  </div>
                )
              };
            }}
            onChange={(value) => handleChangeDeadline(value, false)}
          />
        </div>
      </div>
      <div className='flex h-[40px] w-[100px] items-center'>
        <TimePicker
          format='HH:mm'
          placeholder={FORMAT_TIME}
          value={timeValue}
          className='h-[40px] w-[100px]'
          allowClear
          onChange={handleChangeTime}
          locale={locale}
        />
      </div>
      <div className='flex gap-[8px] items-baseline'>
        <div className='text-lnk text-[14px] font-medium flex items-center w-max'>{t('project:task_of_project:mot_pick:label')}</div>
        <div className='w-[280px] base-form-dot-multiple-select'>
          <FormMultipleSelect<ProjectTaskPic & IOptions>
            name='picId'
            mode='multiple'
            label=''
            maxTagCount={'responsive'}
            options={picOptions().filtered}
            onChange={handleOnChangePic}
            confirmations={{
              condition: handlePicCondition,
              onCancel: handleCancelPic,
              onConfirm: handleConfirmPic
            }}
            tagShowTooltip
            labelInValue
            disabled={detailData?.taskType === CATEGORY.MOT}
            placeholder={detailData?.taskType === CATEGORY.MOT ? '' : t('project:task_of_project:mot_pick:placeholder')}
          />
        </div>
      </div>
      <div className='flex gap-[8px] items-baseline'>
        <div className='text-lnk text-[14px] font-medium flex items-center w-max'>{t('project:task_of_project:status:label')}</div>
        <div className={classNames('shrink-0', isSupportTask ? 'w-[151px]' : 'w-[101px]')}>
          <SelectionPreview options={statusOptions as DefaultOptionType[]} value={watch('status')} label={detailData?.statusName || ''}>
            <FormSelect
              allowClear={false}
              name='status'
              label=''
              options={statusOptions}
              disabled={hasWaitingFile}
              placeholder={t('project:task_of_project:status:placeholder')}
              preChange={handlePreChangeStatus}
            />
          </SelectionPreview>
        </div>
      </div>
      {STATUS_NAME.COMPLETED === detailData?.statusName ? (
        <div className='h-[40px] flex items-center font-normal'>
          {t('project_detail:task_list:completed_date_actual')} : {DataViewer.displayTime(detailData?.completeDate, FORMAT_DATE_EN)}
        </div>
      ) : (
        <div className='h-[40px] flex items-center'>{isTaskDelay && renderTaskStatus(displayStatus, '!w-max px-[8px] py-[2px] !text-base')}</div>
      )}
    </div>
  );
};
